/**
 * DeclarationsPage.js
 *
 * /fly/customs-declarations — after the party: every passenger's customs declaration from Unclaimed Declarations,
 * their two truths and a lie with the lie marked, and how many immigration officers it fooled.
 *
 * Only for guests who were on the flight (signed in, matched by the backend the same way as Souvenirs) and admins,
 * and only once the host has finished the reveal.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Check, Stamp, X } from "lucide-react";
import toast from "react-hot-toast";
import { useClientAuth } from "../../hooks/clientAuth";
import SignUpAndInModal from "../../components/SignUp/SignUpAndInModal";
import { CardBand, CARD_INK, CARD_PAPER } from "./components/DeclarationCard";
import { AfterPartySigns, Banner, bigButton, StatusCard, useGuestColour } from "./components/AfterParty";
import { getDeclarations } from "./helpers/flyApi";
import { useNoIndex } from "./helpers/flyPage";

const INK = "#1B2A41";
const MUTED = "#5B6577";
const PAPER = "#FBF7EF";
const BACKDROP = "#E9E4DA";
const TRUTH = "#1F7A4A";
const LIE = "#B5192E";

const Mark = ({ lie }) => (
    <span className="flex shrink-0 flex-col items-center gap-0.5 w-12">
        <span className="grid place-items-center rounded-full" style={{ width: 26, height: 26, background: lie ? LIE : TRUTH }}>
            {lie ? <X size={16} strokeWidth={3} color="#FFFFFF" aria-hidden="true" /> : <Check size={16} strokeWidth={3} color="#FFFFFF" aria-hidden="true" />}
        </span>
        <span className="text-[10px] font-bold tracking-[0.16em]" style={{ color: lie ? LIE : TRUTH }}>{lie ? "LIE" : "TRUTH"}</span>
    </span>
);

/**
 * One passenger's declaration, with the lie marked
 */
const Declaration = ({ card }) => {
    const fooled = card.reports
        ? `Fooled ${card.fellFor} of ${card.reports} officer${card.reports === 1 ? "" : "s"}`
        : "No reports filed";
    return (
        <article className="rounded-2xl overflow-hidden border-2 flex flex-col text-left" style={{ borderColor: card.mine ? card.colour : CARD_INK, background: CARD_PAPER, color: CARD_INK }}>
            <CardBand number={card.number} compact>
                {card.mine && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold tracking-[0.16em]" style={{ background: card.colour, color: PAPER }}>YOU</span>}
            </CardBand>
            <div className="flex items-center gap-2 px-4 pt-3">
                <span className="size-3 shrink-0 rounded-full" style={{ background: card.colour }} aria-hidden="true" />
                <span className="text-base font-bold tracking-[0.04em] truncate">{card.fullName.toUpperCase()}</span>
                <span className="text-xs font-bold tracking-[0.14em] truncate" style={{ color: MUTED }}>{card.fromCity.toUpperCase()}</span>
            </div>
            <ol className="flex flex-col gap-2 p-4">
                {card.statements.map((statement, index) => {
                    const lie = index === card.lie;
                    return (
                        <li key={index} className="flex items-center gap-3 rounded-xl px-3 py-2.5" style={{ background: lie ? "#F8E3E1" : "#EEF5EF" }}>
                            <Mark lie={lie} />
                            <span className="flex-1 text-[15px] leading-snug">{statement}</span>
                        </li>
                    );
                })}
            </ol>
            <p className="mt-auto px-4 pb-4 text-[11px] font-bold tracking-[0.14em]" style={{ color: MUTED }}>{fooled.toUpperCase()}</p>
        </article>
    );
};

const DeclarationsPage = () => {
    const { user, loading: authLoading, signOutUser } = useClientAuth();
    const colour = useGuestColour();
    const [showSignIn, setShowSignIn] = useState(false);
    // { state: 'loading' | 'found' | 'none' | 'notYet' | 'error', cards?, error? }
    const [result, setResult] = useState({ state: "loading" });
    const [reloadKey, setReloadKey] = useState(0);

    useNoIndex("Customs declarations · Flight YJ030");
    const uid = user?.uid;

    useEffect(() => {
        if (authLoading || !uid) return;
        let ignore = false;
        setResult({ state: "loading" });
        getDeclarations()
            .then((data) => { if (!ignore) setResult({ state: "found", cards: data.cards }); })
            .catch((err) => {
                if (ignore) return;
                if (err.status === 404) setResult({ state: "none" });
                else if (err.status === 409) setResult({ state: "notYet" });
                else setResult({ state: "error", error: err.message || "Something went wrong" });
            });
        return () => { ignore = true; };
    }, [uid, authLoading, reloadKey]);

    // Your own card first, then by card number
    const cards = useMemo(() => (result.cards || []).slice().sort((a, b) => (b.mine - a.mine) || (a.number - b.number)), [result.cards]);

    const handleSignOut = async () => {
        try {
            await signOutUser();
        } catch (err) {
            toast.error("Couldn't sign out.");
        }
    };

    const signedInLine = user && (
        <p className="text-center text-xs" style={{ color: MUTED }}>
            Signed in as {user.email || user.displayName || "you"} ·{" "}
            <button type="button" onClick={handleSignOut} className="underline underline-offset-2">Sign out</button>
        </p>
    );
    const banner = (children) => <Banner icon={Stamp} label="CUSTOMS DECLARATIONS">{children}</Banner>;

    let body;
    if (authLoading || (user && result.state === "loading")) {
        body = (
            <>
                {banner()}
                <p className="pt-24 text-center text-sm font-bold tracking-[0.24em] animate-pulse" style={{ color: MUTED }}>OPENING THE FILES…</p>
            </>
        );
    } else if (!user) {
        body = (
            <>
                {banner()}
                <div className="px-3 pt-8">
                    <StatusCard title="CUSTOMS DECLARATIONS">
                        <Stamp size={40} color="#C8442F" aria-hidden="true" />
                        <h1 className="text-xl font-bold tracking-[0.04em]">Sign in to see everyone's declarations</h1>
                        <p className="text-sm" style={{ color: MUTED }}>For passengers of Flight YJ030 only. Use the same account you used for your invite.</p>
                        <button type="button" onClick={() => setShowSignIn(true)} className={bigButton} style={{ background: INK, color: PAPER }}>SIGN IN</button>
                    </StatusCard>
                </div>
            </>
        );
    } else if (result.state === "none" || result.state === "notYet") {
        body = (
            <>
                {banner()}
                <div className="px-3 pt-8 flex flex-col gap-4">
                    <StatusCard title="CUSTOMS DECLARATIONS">
                        <Stamp size={40} color="#C8442F" aria-hidden="true" />
                        {result.state === "none" ? (
                            <>
                                <h1 className="text-xl font-bold">We couldn't find your booking on Flight YJ030.</h1>
                                <p className="text-sm" style={{ color: MUTED }}>The declarations are for the flight's passengers. Flew with a different account? Sign out and try that one.</p>
                            </>
                        ) : (
                            <>
                                <h1 className="text-xl font-bold">The files are still sealed.</h1>
                                <p className="text-sm" style={{ color: MUTED }}>Everyone's declarations open once the captain finishes the reveal.</p>
                            </>
                        )}
                    </StatusCard>
                    {signedInLine}
                </div>
            </>
        );
    } else if (result.state === "error") {
        body = (
            <>
                {banner()}
                <div className="pt-16 px-4 flex flex-col items-center gap-4 text-center">
                    <p className="font-bold">We couldn't open the declarations.</p>
                    <p className="text-sm" style={{ color: MUTED }}>{result.error}</p>
                    <button type="button" onClick={() => setReloadKey((k) => k + 1)} className="min-h-[44px] text-xs font-bold tracking-[0.18em] underline underline-offset-4">TRY AGAIN</button>
                    {signedInLine}
                </div>
            </>
        );
    } else {
        body = (
            <>
                {banner(
                    <>
                        <h1 className="text-3xl sm:text-4xl font-bold mt-1">Two truths and a lie</h1>
                        <p className="text-sm max-w-md" style={{ color: MUTED }}>
                            Every passenger's declaration from Flight YJ030, with the lie marked. {cards.length} declaration{cards.length === 1 ? "" : "s"}.
                        </p>
                    </>
                )}
                <div className="mx-auto mt-6 grid w-full max-w-5xl gap-4 px-3 sm:grid-cols-2">
                    {cards.map((card) => <Declaration key={card.number} card={card} />)}
                </div>
                <div className="mt-6">{signedInLine}</div>
            </>
        );
    }

    return (
        <div className="min-h-screen w-full overflow-x-hidden pb-14" style={{ background: BACKDROP, color: INK, fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
            {body}
            <div className="mx-auto mt-10 w-full max-w-xl px-3">
                <AfterPartySigns current="declarations" colour={colour} />
            </div>
            <SignUpAndInModal isOpen={showSignIn} onClose={() => setShowSignIn(false)} />
        </div>
    );
};

export default DeclarationsPage;
